import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";
import type {
  CreateGrowInput,
  DiagnoseGrowInput,
  Grow,
  GrowDiagnosis,
  ListResponse,
  UpdateGrowInput,
} from "./types";

const base = "/v1/grows";

export interface ListGrowsParams {
  include_archived?: boolean;
  limit?: number;
  offset?: number;
}

export function useGrows(params: ListGrowsParams = {}) {
  return useQuery({
    queryKey: queryKeys.grows.list(params as Record<string, unknown>),
    queryFn: ({ signal }) =>
      api.get<ListResponse<Grow>>(
        base,
        {
          include_archived: params.include_archived ? "1" : undefined,
          limit: params.limit,
          offset: params.offset,
        },
        signal,
      ),
  });
}

export function useGrow(id: string | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.grows.detail(id) : queryKeys.grows.all,
    queryFn: ({ signal }) => api.get<Grow>(`${base}/${id}`, undefined, signal),
    enabled: !!id,
  });
}

export function useCreateGrow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGrowInput) => api.post<Grow>(base, input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: queryKeys.grows.all });
      qc.setQueryData(queryKeys.grows.detail(created.id), created);
    },
  });
}

export function useUpdateGrow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateGrowInput) =>
      api.patch<Grow>(`${base}/${id}`, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.grows.detail(id), updated);
      qc.invalidateQueries({ queryKey: queryKeys.grows.all });
    },
  });
}

export function useArchiveGrow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | undefined) =>
      api.delete<void>(`${base}/${id}`, { reason: reason ?? "" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.grows.all });
    },
  });
}

// useDiagnoseGrow asks the server's AI to assess a grow against its targets.
// Display-only — the caller decides which suggested actions to create.
export function useDiagnoseGrow(id: string) {
  return useMutation({
    mutationFn: (input: DiagnoseGrowInput) =>
      api.post<GrowDiagnosis>(`${base}/${id}/ai/diagnose`, input),
  });
}
